import clsx from "clsx";
import { RefObject, useLayoutEffect, useRef, useState } from "react";

import { ContextMenu } from "@smart/itops-sb-design-system-dom";
import { toSentenceCase } from "@smart/itops-utils-basic";

import { PickerSelection, PickerSelectionComponent } from "./types";

const levelIndicatorClassNames =
  "relative before:content-[''] before:absolute before:w-full before:bottom-[-1.2rem] before:border-y before:border-neutral-500";
const crumbClassNames = clsx(
  "enabled:text-blue-560 after:content-['/'] after:mx-[0.5rem]",
  levelIndicatorClassNames,
);

type PickerCrumbsProps = {
  crumbs: PickerSelectionComponent[] | undefined;
  setSelection: (selection: PickerSelection) => void;
  isRoleComponentSelected: boolean;
  roleComponentIndex: number;
  isRoleProviderLayout: boolean;
  isInGroup: boolean | undefined;
  selection: PickerSelection;
  fixedSelection?: PickerSelection;
  canAddChildSubLayout: boolean | undefined;
};

export const PickerCrumbs = ({
  selection,
  crumbs,
  setSelection,
  isRoleComponentSelected,
  roleComponentIndex,
  isRoleProviderLayout,
  isInGroup,
  fixedSelection,
  canAddChildSubLayout,
}: PickerCrumbsProps) => {
  const [numOfHiddenCrumbs, setNumOfHiddenCrumbs] = useState(0);
  const firstComponentRef = useRef<HTMLDivElement | HTMLButtonElement>(null);
  const lastComponentRef = useRef<HTMLButtonElement>(null);

  const isCrumbsWrapped = () => {
    const firstOffset = firstComponentRef.current?.offsetTop;
    const lastOffset = lastComponentRef.current?.offsetTop;
    return firstOffset && lastOffset && firstOffset !== lastOffset;
  };

  useLayoutEffect(() => {
    if (!crumbs?.length) return setNumOfHiddenCrumbs(0);

    if (numOfHiddenCrumbs === 0 && isCrumbsWrapped()) {
      setNumOfHiddenCrumbs(1);
    } else {
      setNumOfHiddenCrumbs(0);
    }
    return undefined;
  }, [crumbs?.length]);

  useLayoutEffect(() => {
    if (isCrumbsWrapped()) {
      setNumOfHiddenCrumbs((existing) => existing + 1);
    }
  }, [numOfHiddenCrumbs]);

  // If it is a role provider layout, the top folder is a Role folder.
  const isChildOfRoleFolder = (componentIndex: number) =>
    isRoleComponentSelected
      ? roleComponentIndex <= componentIndex
      : isRoleProviderLayout;
  const isLastComponent = (index: number) =>
    crumbs && index === crumbs.length - 1;

  const goToRoot = () =>
    setSelection({
      layout: undefined,
      prefix: [],
      fields: [],
      components: [],
    });
  const goToSelected = (componentIndex: number) => {
    if (!isLastComponent(componentIndex) && crumbs) {
      const removedCrumbs = crumbs.slice(componentIndex + 1);
      setSelection({
        layout: selection.layout,
        prefix: selection.prefix.slice(0, componentIndex),
        fields:
          isChildOfRoleFolder(componentIndex) && isInGroup
            ? selection.fields
            : [],
        components: selection.components.filter(
          (c) => !removedCrumbs.find((rc) => c.name === rc.name),
        ),
      });
    }
  };

  const isCrumbDisabled = (
    crumb: PickerSelectionComponent,
    index: number,
  ): boolean => {
    if (!fixedSelection || isChildOfRoleFolder(index)) return false;
    const fixedSelectionPathComponents = fixedSelection.components.slice(
      0,
      fixedSelection.components.length - 1,
    );

    return !!fixedSelectionPathComponents.find((c) => c.name === crumb.name);
  };

  return (
    <div
      className="flex flex-wrap px-[2.4rem] py-[1.1rem] text-paragraph text-neutral-850 border-b border-neutral-100"
      data-testid="picker-crumbs"
    >
      {numOfHiddenCrumbs > 0 ? (
        <div
          className={clsx("inline-flex", levelIndicatorClassNames)}
          ref={firstComponentRef as RefObject<HTMLDivElement>}
        >
          <ContextMenu
            button={{
              className: "w-[2.4rem] h-[2.4rem]",
              size: "small",
              variant: "secondarySubtle",
              leftIcon: "solidEllipsis",
            }}
            placement="bottom-end"
            offsetBy={5}
            items={[
              {
                label: "Mapped fields",
                onClick: goToRoot,
                disabled: !!fixedSelection,
              },
              ...(crumbs
                ?.slice(0, numOfHiddenCrumbs - 1)
                .map((component, componentIndex) => ({
                  label: toSentenceCase(component.displayName),
                  onClick: () => goToSelected(componentIndex),
                  disabled: isCrumbDisabled(component, componentIndex),
                })) || []),
            ]}
          />
          <span className="mx-[0.5rem] text-blue-560">/</span>
        </div>
      ) : (
        <button
          ref={firstComponentRef as RefObject<HTMLButtonElement>}
          className={crumbClassNames}
          type="button"
          disabled={canAddChildSubLayout ? false : !!fixedSelection}
          onClick={goToRoot}
        >
          Mapped fields
        </button>
      )}
      {crumbs?.map((component, componentIndex) => (
        <button
          ref={isLastComponent(componentIndex) ? lastComponentRef : null}
          className={clsx(
            crumbClassNames,
            "last:after:hidden",
            componentIndex <= numOfHiddenCrumbs - 2 && "hidden",
          )}
          type="button"
          // eslint-disable-next-line react/no-array-index-key
          key={`${component.type}-${componentIndex}`}
          disabled={
            isCrumbDisabled(component, componentIndex) ||
            isLastComponent(componentIndex)
          }
          onClick={() => goToSelected(componentIndex)}
        >
          {toSentenceCase(component.displayName)}
        </button>
      ))}
    </div>
  );
};
